import type { AddUserProductInput } from '@front-activerse/common-components';
import { Ingredient } from '../types';

export const SCAN_LABEL_RESULT_STORAGE_KEY = 'tracker_scan_label_result';
export const SCAN_LABEL_RESULT_EVENT = 'tracker_scan_label_result';

// Each mode ('Per 100' / 'Per serving') owns its OWN independent set of macro inputs.
// Switching modes only swaps which set is shown — values are never recalculated on the fly.
// Prefill from a scan may populate both sets at once (see scanResultToFormValues).
export interface MacroValues {
  calories: string;
  protein: string;
  fat: string;
  carbs: string;
  fiber: string;
}

export interface AddUserProductFormValues {
  name: string;
  brand: string;
  unit: 'GRAMS' | 'MILLILITERS';
  mode: 'PER_100_UNITS' | 'PER_SERVING';
  servingSize: string;
  per100: MacroValues;
  perServing: MacroValues;
}

const emptyMacros: MacroValues = {
  calories: '',
  protein: '',
  fat: '',
  carbs: '',
  fiber: '',
};

export const emptyFormValues: AddUserProductFormValues = {
  name: '',
  brand: '',
  unit: 'GRAMS',
  mode: 'PER_100_UNITS',
  servingSize: '',
  per100: { ...emptyMacros },
  perServing: { ...emptyMacros },
};

export const parseDecimal = (value: string): number => parseFloat(value.replace(',', '.'));

// Atwater calorie factors (USDA), matching ai-flows' nutrition-validator: fiber is indigestible
// carbohydrate at ~2 kcal/g, so digestible carbs = total carbs - fiber.
const ATWATER = { protein: 4, fat: 9, carbs: 4, fiber: 2 } as const;

// Warn (but don't block) when stated calories drift from the macro-derived estimate by more than
// this fraction — deliberately looser than the backend's hard 10% gate.
export const CALORIE_MISMATCH_THRESHOLD = 0.25;

export const calculateCaloriesFromMacros = (macros: MacroValues): number => {
  const protein = parseDecimal(macros.protein) || 0;
  const fat = parseDecimal(macros.fat) || 0;
  const carbs = parseDecimal(macros.carbs) || 0;
  const fiber = parseDecimal(macros.fiber) || 0;
  const digestibleCarbs = Math.max(0, carbs - fiber);
  return (
    protein * ATWATER.protein +
    fat * ATWATER.fat +
    digestibleCarbs * ATWATER.carbs +
    fiber * ATWATER.fiber
  );
};

// Returns the relative calorie mismatch (0..1+) for the ACTIVE mode's set, or null when it can't
// be computed (missing/zero stated calories). Compared against CALORIE_MISMATCH_THRESHOLD to
// decide whether to surface the confirm-again warning.
export const caloriesMismatchRatio = (values: AddUserProductFormValues): number | null => {
  const macros = values.mode === 'PER_SERVING' ? values.perServing : values.per100;
  const stated = parseDecimal(macros.calories);
  if (!Number.isFinite(stated) || stated <= 0) return null;
  const calculated = calculateCaloriesFromMacros(macros);
  return Math.abs(stated - calculated) / stated;
};

export const toMutationInput = (
  values: AddUserProductFormValues,
  ean?: string
): AddUserProductInput => {
  // Send only the macros of the active mode; the backend normalizes per-serving to per-100.
  const macros = values.mode === 'PER_SERVING' ? values.perServing : values.per100;

  // In PER_100_UNITS mode the serving size (if known, e.g. captured from a scan) is sent as
  // standalone product metadata so it isn't lost — the backend ignores it in PER_SERVING mode,
  // where `nutritionValues.servingSize` already carries it.
  const servingQuantity =
    values.mode === 'PER_100_UNITS' && values.servingSize.trim() !== ''
      ? parseDecimal(values.servingSize)
      : null;

  return {
    name: values.name.trim(),
    ...(values.brand.trim() !== '' && { brand: values.brand.trim() }),
    ...(ean && { ean }),
    unit: values.unit,
    nutritionValues: {
      mode: values.mode,
      ...(values.mode === 'PER_SERVING' && { servingSize: parseDecimal(values.servingSize) }),
      calories: parseDecimal(macros.calories),
      protein: parseDecimal(macros.protein),
      fat: parseDecimal(macros.fat),
      carbohydrates: parseDecimal(macros.carbs),
      ...(macros.fiber.trim() !== '' && { fiber: parseDecimal(macros.fiber) }),
    },
    ...(servingQuantity != null &&
      Number.isFinite(servingQuantity) &&
      servingQuantity > 0 && { servingQuantity }),
  };
};

export interface ScanPrimaryIngredient {
  productId?: string;
  name: string;
  brand?: string;
  baseAmount: number;
  baseUnit: string;
  calories: number;
  proteinGrams: number;
  carbsGrams: number;
  fatGrams: number;
  fiberGrams?: number | null;
  servingQuantity?: number | null;
  servingUnit?: string | null;
}

const toFormNumber = (value: number | null | undefined): string =>
  value == null ? '' : String(value);

// Round a per-serving macro to at most 2 decimals and drop a trailing ".00"/".x0".
const scaleMacro = (per100: number | null | undefined, factor: number): string => {
  if (per100 == null) return '';
  return String(Math.round(per100 * factor * 100) / 100);
};

export const scanResultToFormValues = (
  primary: ScanPrimaryIngredient,
  current: AddUserProductFormValues
): AddUserProductFormValues => {
  const unit: AddUserProductFormValues['unit'] =
    primary.baseUnit === 'milliliters' ? 'MILLILITERS' : 'GRAMS';

  const per100: MacroValues = {
    calories: toFormNumber(primary.calories) || current.per100.calories,
    protein: toFormNumber(primary.proteinGrams) || current.per100.protein,
    fat: toFormNumber(primary.fatGrams) || current.per100.fat,
    carbs: toFormNumber(primary.carbsGrams) || current.per100.carbs,
    fiber: toFormNumber(primary.fiberGrams) || current.per100.fiber,
  };

  // Fill the per-serving set only when the label gave a serving size in the SAME unit as the
  // per-100 values — otherwise the scaling factor would be meaningless. Values are computed
  // once here (per100 * serving/100), never recalculated live on mode switch.
  const servingUnit = primary.servingUnit === 'milliliters' ? 'MILLILITERS' : 'GRAMS';
  const canScaleServing =
    primary.servingQuantity != null &&
    primary.servingQuantity > 0 &&
    primary.servingUnit != null &&
    servingUnit === unit;

  const perServing: MacroValues = canScaleServing
    ? {
        calories: scaleMacro(primary.calories, primary.servingQuantity! / 100),
        protein: scaleMacro(primary.proteinGrams, primary.servingQuantity! / 100),
        fat: scaleMacro(primary.fatGrams, primary.servingQuantity! / 100),
        carbs: scaleMacro(primary.carbsGrams, primary.servingQuantity! / 100),
        fiber: scaleMacro(primary.fiberGrams, primary.servingQuantity! / 100),
      }
    : current.perServing;

  return {
    ...current,
    name: primary.name || current.name,
    brand: primary.brand || current.brand,
    unit,
    mode: 'PER_100_UNITS',
    servingSize: canScaleServing ? toFormNumber(primary.servingQuantity) : current.servingSize,
    per100,
    perServing,
  };
};

const UNIT_META: Record<'GRAMS' | 'MILLILITERS', { suffix: string; measurementUnit: string }> = {
  GRAMS: { suffix: 'g', measurementUnit: 'grams' },
  MILLILITERS: { suffix: 'ml', measurementUnit: 'milliliters' },
};

export const addedProductToIngredient = (
  added: { id: string | number; name: string; brand?: string | null },
  values: AddUserProductFormValues,
  multiplier: number
): Ingredient => {
  const unitMeta = UNIT_META[values.unit];
  const macros = values.mode === 'PER_SERVING' ? values.perServing : values.per100;
  const baseAmount = values.mode === 'PER_SERVING' ? parseDecimal(values.servingSize) : 100;

  const amountFor1Portion = baseAmount / multiplier;

  return {
    id: String(added.id),
    name: added.name,
    ...(added.brand && { brand: added.brand }),
    kcal: parseDecimal(macros.calories) / multiplier,
    measurements: [
      {
        amount: amountFor1Portion,
        text: `${amountFor1Portion} ${unitMeta.suffix}`,
        unit: unitMeta.measurementUnit,
      },
    ],
    macro: {
      protein: { grams: parseDecimal(macros.protein) / multiplier },
      fat: { grams: parseDecimal(macros.fat) / multiplier },
      carbohydrates: { grams: parseDecimal(macros.carbs) / multiplier },
    },
  };
};

// Maps a backend ValidationException fieldName to the matching Formik field path. Non-macro
// fields map directly; macros are submitted from the active mode's set only, so their errors
// resolve to `${mode}.field` (per100/perServing).
const FIELD_NAME_MAP: Record<string, string> = {
  name: 'name',
  brand: 'brand',
  ean: 'name', // ean is not an input; surface its error on the name field
  unit: 'unit',
  servingSize: 'servingSize',
};

const MACRO_FIELD_MAP: Record<string, keyof MacroValues> = {
  calories: 'calories',
  protein: 'protein',
  fat: 'fat',
  carbohydrates: 'carbs',
  fiber: 'fiber',
};

export const resolveErrorField = (
  backendFieldName: string,
  mode: AddUserProductFormValues['mode']
): string | null => {
  if (FIELD_NAME_MAP[backendFieldName]) return FIELD_NAME_MAP[backendFieldName];
  const macroField = MACRO_FIELD_MAP[backendFieldName];
  if (macroField) {
    const group = mode === 'PER_SERVING' ? 'perServing' : 'per100';
    return `${group}.${macroField}`;
  }
  return null;
};
